import { useLocation, Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  ChartBar,
  Buildings,
  FileText,
  Stamp,
  FileArrowUp,
  FileArrowDown,
  CheckSquare,
  CurrencyDollar,
  Calculator,
  CloudArrowUp,
  ArrowsLeftRight,
  Bell,
  ChartPie,
  Envelope,
  CalendarDots,
  ListChecks,
  Notebook,
  Receipt,
  SignOut,
} from "@phosphor-icons/react";

const navGroups = [
  {
    label: "",
    items: [{ to: "/", icon: ChartBar, label: "Dashboard" }, { to: "/deadlines", icon: Bell, label: "Deadlines" }, { to: "/digest", icon: Envelope, label: "Digest Preview" }],
  },
  {
    label: "Management",
    items: [
      { to: "/clients", icon: Buildings, label: "Clients" },
      { to: "/client-contracts", icon: FileText, label: "Client Contracts" },
      { to: "/client-invoices", icon: Receipt, label: "Client Invoices" },
      { to: "/contracts", icon: FileText, label: "E-Rate Contracts" },
    ],
  },
  {
    label: "E-Rate Forms",
    items: [
      { to: "/forms/470", icon: FileArrowUp, label: "Form 470" },
      { to: "/forms/471", icon: FileArrowDown, label: "Form 471 / PIA" },
      { to: "/forms/486", icon: CheckSquare, label: "Form 486" },
      { to: "/frn-status", icon: ListChecks, label: "FRN Status" },
      { to: "/frn-lifecycle", icon: ArrowsLeftRight, label: "FRN Lifecycle" },
    ],
  },
  {
    label: "Financials",
    items: [
      { to: "/invoices", icon: CurrencyDollar, label: "Invoices (472/474)" },
      { to: "/c2-budget", icon: Calculator, label: "C2 Budget" },
    ],
  },
  {
    label: "Data",
    items: [
      { to: "/usac-import", icon: CloudArrowUp, label: "USAC Import" },
      { to: "/reports", icon: ChartPie, label: "Report Builder" },
      { to: "/reports/client-funding", icon: Notebook, label: "Client Funding" },
      { to: "/reports/2027-470", icon: FileArrowDown, label: "2027 470 Reports" },
    ],
  },
];

export default function Layout({ children, onLogout }) {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const jwtUser = JSON.parse(localStorage.getItem("auth_user") || "null");
  const userName = accounts?.[0]?.name || jwtUser?.name || jwtUser?.email || "";

  const handleLogout = () => {
    if (accounts.length > 0) {
      instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    if (onLogout) onLogout();
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-[240px] h-full bg-white border-r border-gray-200 flex flex-col flex-shrink-0 overflow-y-auto" data-testid="sidebar">
        <div className="px-4 py-5 border-b border-gray-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
              <span className="text-white font-bold text-sm" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>ER</span>
            </div>
            <div>
              <h1 className="text-sm font-bold text-[#1A1D20] tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>E-RATE CRM</h1>
              <p className="text-[10px] text-[#6B7280] tracking-[0.15em] uppercase">Consultant Tool</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-2">
          {navGroups.map((group, gi) => (
            <div key={gi}>
              {group.label && (
                <div className="sidebar-section-title">{group.label}</div>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                    className={`sidebar-link ${isActive ? "active" : ""}`}
                  >
                    <Icon size={18} weight={isActive ? "bold" : "regular"} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200">
          {userName && (
            <p className="text-xs text-[#1A1D20] font-medium mb-2 truncate" title={userName}>{userName}</p>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 text-xs text-[#6B7280] hover:text-[#FF2A00] transition-colors py-1"
          >
            <SignOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-y-auto bg-[#F8F9FA]" data-testid="main-content">
        <div className="flex-1">{children}</div>
        <footer data-testid="app-footer" className="mt-auto border-t border-gray-200 bg-white px-6 py-3 text-center">
          <p className="text-xs text-[#6B7280] tracking-wider">
            <span className="font-semibold text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Nexus E-Rate Services</span>
            <span className="mx-2 text-gray-300">|</span>
            <span>© {new Date().getFullYear()} All rights reserved</span>
          </p>
        </footer>
      </main>
    </div>
  );
}
